import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  RefreshCw,
  Send,
  Users,
  UserRoundPlus,
} from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { AdminPageHeader, StateBlock } from "./components/AdminUi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/commerciale`;
// Scala di verdi/neutri coerente con l'accento admin (niente colore diverso per stato).
const PIPELINE_COLORS = ["#14532d", "#166534", "#15803d", "#16a34a", "#4ade80", "#94a3b8", "#475569"];

function number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value) {
  if (!value) return "Data non disponibile";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function followupDate(followup) {
  return (
    followup.data_prevista ||
    followup.data_scadenza ||
    followup.due_date ||
    followup.scheduled_at ||
    followup.data_followup ||
    followup.date ||
    followup.created_at
  );
}

function followupTitle(followup) {
  return (
    followup.titolo ||
    followup.title ||
    followup.nome_lead ||
    followup.lead_nome ||
    followup.lead_name ||
    followup.testo ||
    "Follow-up commerciale"
  );
}

function followupDescription(followup) {
  return followup.note || followup.descrizione || followup.description || followup.tipo || "";
}

function isLate(followup) {
  const value = followupDate(followup);
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.getTime() < Date.now();
}

function KpiCard({ icon: Icon, label, value, hint, servizio }) {
  return (
    <Card className="border-admin-border shadow-sm">
      <CardContent className="flex items-start justify-between gap-2 p-4">
        <div className="min-w-0">
          <p className="text-xs font-medium leading-snug text-admin-muted">{label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-admin-text">
            {value === undefined || value === null ? "—" : number(value).toLocaleString("it-IT")}
          </p>
          {servizio ? <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-admin-muted">{servizio}</p> : null}
          {hint ? <p className="mt-0.5 text-[11px] leading-snug text-admin-muted">{hint}</p> : null}
        </div>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-admin-accent-soft text-admin-accent">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
      </CardContent>
    </Card>
  );
}

// Servizio di ciascun KPI per il filtro Tutti / CDRPT / Affidya.
const KPI_DEFS = [
  { key: "nuovi_lead", label: "Nuovi lead strutture", icon: UserRoundPlus, servizio: "affidya", hint: "Stato Nuovo" },
  { key: "demo_aperte", label: "Demo aperte", icon: CalendarClock, servizio: "affidya", hint: "Stato Programmata" },
  { key: "followup_scaduti", label: "Follow-up scaduti", icon: AlertCircle, servizio: "tutti", hint: "Attività aperte oltre la data prevista" },
  { key: "richieste_famiglie", label: "Richieste famiglie nuove", icon: Users, servizio: "cdrpt", hint: "Escluse pratiche rete e gettoni" },
  { key: "inviti_in_attesa", label: "Inviti in attesa", icon: Send, servizio: "affidya", hint: "Non scaduti" },
  { key: "conversioni", label: "Conversioni di oggi", icon: CheckCircle2, servizio: "affidya", hint: "Lead diventati Cliente oggi" },
];
const SERVIZIO_LABEL = { affidya: "R&CO. / Affidya", cdrpt: "CasaDiRiposoPerTe", tutti: "Entrambi i servizi" };

export default function CommercialeDashboardModule({ embedded = false, servizio = "tutti" }) {
  const [data, setData] = useState({ kpi: {}, pipeline: [], followups: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("super_admin_token");
      const response = await fetch(`${API}/stats/dashboard`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.detail || "Impossibile caricare la dashboard commerciale");
      setData({
        kpi: json.kpi || {},
        pipeline: Array.isArray(json.pipeline) ? json.pipeline : [],
        followups: Array.isArray(json.followups) ? json.followups : [],
      });
    } catch (err) {
      setError(err.message || "Errore nel caricamento della dashboard commerciale");
      toast.error(err.message || "Errore nel caricamento della dashboard commerciale");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const pipeline = useMemo(
    () =>
      data.pipeline.map((item, index) => ({
        name: item._id || "Non classificato",
        value: number(item.count),
        color: PIPELINE_COLORS[index % PIPELINE_COLORS.length],
      })),
    [data.pipeline]
  );
  const pipelineTotal = pipeline.reduce((total, item) => total + item.value, 0);
  const followups = useMemo(
    () => [...data.followups].sort((a, b) => new Date(followupDate(a) || 0) - new Date(followupDate(b) || 0)),
    [data.followups]
  );
  const lateFollowups = followups.filter(isLate);
  const upcomingFollowups = followups.filter((item) => !isLate(item));

  const refreshButton = (
    <Button type="button" variant="outline" size="sm" onClick={loadDashboard} disabled={loading}>
      <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
      Aggiorna
    </Button>
  );
  const kpis = KPI_DEFS.filter((k) => servizio === "tutti" || k.servizio === servizio || k.servizio === "tutti");
  const showAffidyaBlocks = servizio !== "cdrpt";

  return (
    <div className="space-y-5">
      {embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-admin-muted">Situazione attuale di lead, demo, follow-up e conversioni.</p>
          {refreshButton}
        </div>
      ) : (
        <AdminPageHeader
          icon={ClipboardList}
          title="Dashboard commerciale"
          description="Panoramica di lead, demo, follow-up e conversioni."
          actions={refreshButton}
        />
      )}

      {error && !loading ? (
        <StateBlock state="error" description={error} onRetry={loadDashboard} />
      ) : (
      <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {kpis.map((k) => (
          <KpiCard
            key={k.key}
            icon={k.icon}
            label={k.label}
            value={loading ? undefined : data.kpi[k.key]}
            hint={k.hint}
            servizio={SERVIZIO_LABEL[k.servizio]}
          />
        ))}
      </div>

      {showAffidyaBlocks ? (
      <div className="grid gap-4 xl:grid-cols-5">
        <Card className="xl:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Pipeline Affidya</CardTitle>
            <p className="text-xs text-slate-500">{pipelineTotal} opportunità complessive</p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex h-64 items-center justify-center text-sm text-slate-500">Caricamento dati…</div>
            ) : pipeline.length ? (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="h-52 w-full sm:w-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pipeline} dataKey="value" nameKey="name" innerRadius={45} outerRadius={78} paddingAngle={2}>
                        {pipeline.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, "Opportunità"]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="min-w-0 flex-1 space-y-2 text-sm">
                  {pipeline.map((item) => (
                    <li key={item.name} className="flex items-center justify-between gap-3">
                      <span className="flex min-w-0 items-center gap-2 text-slate-600">
                        <i className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="truncate">{item.name}</span>
                      </span>
                      <span className="font-semibold text-slate-900">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="py-16 text-center text-sm text-slate-500">Nessun dato della pipeline disponibile.</p>
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Follow-up da gestire</CardTitle>
            <p className="text-xs text-slate-500">Scadenze imminenti e attività oltre la data prevista.</p>
          </CardHeader>
          <CardContent className="grid gap-5 md:grid-cols-2">
            <FollowupList title="In ritardo" items={lateFollowups} late />
            <FollowupList title="Prossimi" items={upcomingFollowups} />
          </CardContent>
        </Card>
      </div>
      ) : (
        <p className="rounded-xl border border-dashed border-admin-border p-4 text-sm text-admin-muted">
          Pipeline e follow-up riguardano i lead strutture R&CO./Affidya. Le prossime attività CDRPT saranno gestite nella
          scheda Richiesta famiglia (task Pipeline CDRPT).
        </p>
      )}
      </>
      )}
    </div>
  );
}

function FollowupList({ title, items, late = false }) {
  return (
    <section>
      <h3 className={`mb-2 text-sm font-semibold ${late ? "text-amber-800" : "text-slate-700"}`}>
        {title} <span className="text-slate-400">({items.length})</span>
      </h3>
      {!items.length ? (
        <p className="rounded-lg border border-dashed p-3 text-sm text-slate-500">
          {late ? "Nessun follow-up scaduto." : "Nessun prossimo follow-up."}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((followup, index) => (
            <li
              key={followup.id || followup._id || `${followupTitle(followup)}-${index}`}
              className={`rounded-lg border p-3 ${late ? "border-amber-200 bg-amber-50/60" : "border-slate-200 bg-slate-50/60"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-slate-800">{followupTitle(followup)}</p>
                <time className={`shrink-0 text-xs ${late ? "text-amber-800" : "text-slate-500"}`}>
                  {formatDate(followupDate(followup))}
                </time>
              </div>
              {followupDescription(followup) ? (
                <p className="mt-1 line-clamp-2 text-xs text-slate-600">{followupDescription(followup)}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
